import React from 'react'
import { kuiperClassNames, renderIcon } from '@opallabs/kuiper'
import { AccountName } from '../../../../../AccountName'
import { ContentAvatar } from '../../../../../ContentAvatar'
import { ContentTextInput } from '../../../../../ContentTextInput'
import { FacebookReelOverlayProps } from '../../types'
import * as styles from './styles'

export const Overlay: React.FC<FacebookReelOverlayProps> = ({
  accountImage = null,
  accountName,
  collabEnabled,
  collabAccount,
  content,
  contentCharacterLimit,
  contentPlaceholderText,
  footerRef,
  location,
  readOnly,
  sound,
  truncate,
  onUpdateContentOption,
  setIsHovered,
}) => {
  return (
    <div
      css={styles.overlay(truncate)}
      className={kuiperClassNames('mobileSingle-overlay')}
    >
      <div
        css={styles.overlayHeader}
        className={kuiperClassNames('mobileSingle-overlay-header')}
      >
        <div css={styles.headerLeftContainer}>
          {renderIcon('facebook-mobile-reel-chevron-left', {
            height: 20,
            width: 20,
            ariaMode: 'hidden',
            style: { marginRight: 20 },
          })}
          <span aria-hidden css={styles.overlayTitle}>
            Reels
          </span>
          {renderIcon('facebook-mobile-reel-chevron-down', {
            height: 8,
            width: 8,
            ariaMode: 'hidden',
            style: { marginLeft: 8 },
          })}
        </div>
        <div css={styles.headerRightContainer}>
          {renderIcon('facebook-mobile-reel-search', {
            height: 22,
            width: 20,
            ariaMode: 'hidden',
            style: { marginTop: 2 },
          })}
          {renderIcon('facebook-mobile-reel-camera', {
            height: 22,
            width: 22,
            ariaMode: 'hidden',
          })}
          {renderIcon('facebook-mobile-reel-user', {
            height: 22,
            width: 22,
            ariaMode: 'hidden',
          })}
        </div>
      </div>

      <div
        className={kuiperClassNames('mobileSingle-overlay-action-container')}
        css={styles.columnActionContainer}
      >
        {renderIcon('facebook-mobile-like-outlined', {
          height: 24,
          width: 24,
          ariaMode: 'hidden',
          style: styles.columnActionIcon,
        })}
        <span aria-hidden css={styles.iconNumberText}>
          ###
        </span>
        {renderIcon('facebook-mobile-comment-outlined', {
          height: 24,
          width: 24,
          ariaMode: 'hidden',
          style: styles.columnActionIcon,
        })}
        <span aria-hidden css={styles.iconNumberText}>
          ###
        </span>
        {renderIcon('facebook-mobile-share-outlined', {
          height: 24,
          width: 24,
          ariaMode: 'hidden',
          style: styles.columnActionIcon,
        })}
        <span aria-hidden css={styles.iconNumberText}>
          ###
        </span>
        {renderIcon('facebook-mobile-story-more', {
          width: 20,
          ariaMode: 'hidden',
          style: { ...styles.columnActionIcon, marginRight: 3 },
        })}
      </div>

      <div
        className={kuiperClassNames('mobileSingle-overlay-footer')}
        css={styles.footer(readOnly)}
        ref={footerRef}
        onMouseEnter={() => setIsHovered && setIsHovered(false)}
      >
        <div
          className={kuiperClassNames('mobileSingle-overlay-account', {
            [styles.withCollab]: collabEnabled,
          })}
          css={styles.account}
        >
          <div css={styles.avatarContainer}>
            <ContentAvatar assetReference={accountImage} size={35} />
            {collabEnabled && (
              <ContentAvatar
                assetReference={null}
                size={20}
                style={styles.collabAvatarSecondary}
              />
            )}
          </div>
          <div css={styles.accountNameWrapper}>
            <AccountName
              name={accountName}
              color="white"
              style={styles.accountName}
            />
            {collabEnabled && (
              <React.Fragment>
                <span css={styles.collabSeparator}> and </span>
                <AccountName
                  name={collabAccount || 'No collab account'}
                  color={collabAccount ? 'white' : 'orange'}
                  style={styles.accountName}
                />
              </React.Fragment>
            )}
          </div>
          {!collabEnabled && (
            <React.Fragment>
              <div css={styles.globeIcon}>
                {renderIcon('facebook-mobile-globe', {
                  ariaMode: 'hidden',
                  height: 12,
                })}
              </div>
              <div css={styles.followButton}>
                <span aria-hidden>Follow</span>
              </div>
            </React.Fragment>
          )}
        </div>

        <div
          className={kuiperClassNames('mobileSingle-overlay-caption')}
          css={styles.captionContainer(!sound && !location, readOnly)}
          onBlur={() => onUpdateContentOption('content', content)}
        >
          <ContentTextInput
            characterCounterOptions={{ zIndex: 9999 }}
            characterLimit={contentCharacterLimit}
            highlightSocialTags={false}
            maxRows={12}
            placeholder={contentPlaceholderText}
            placeholderStyle={styles.captionPlaceholder}
            readOnly={readOnly || truncate}
            style={styles.caption(truncate, readOnly)}
            truncate={truncate}
            truncationLimit={Math.min(content?.split('\n')[0]?.length ?? 0, 40)}
            value={truncate ? content.split('\n')[0] : content}
            onChange={(value) => onUpdateContentOption('content', value)}
          />
        </div>
        <div css={styles.footerOptionsContainer}>
          {(!readOnly || sound) && (
            <div
              className={kuiperClassNames('mobileSingle-overlay-sound')}
              css={styles.optionTextContainer(Boolean(readOnly || truncate))}
            >
              {renderIcon('facebook-mobile-reel-sound', {
                height: 11,
                ariaMode: 'hidden',
                style: styles.optionIcon,
              })}
              <input
                aria-label="Content Sound"
                placeholder="Add sound..."
                readOnly={truncate || readOnly}
                value={sound || ''}
                onBlur={() => onUpdateContentOption('sound', sound)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                  onUpdateContentOption('sound', e.target.value)
                }
              />
            </div>
          )}
          {(!readOnly || location) && (
            <div
              className={kuiperClassNames('mobileSingle-overlay-location')}
              css={styles.optionTextContainer(Boolean(readOnly || truncate))}
            >
              {renderIcon('facebook-mobile-reel-location', {
                height: 11,
                ariaMode: 'hidden',
                style: styles.optionIcon,
              })}
              <input
                aria-label="Content Location"
                placeholder="Add location..."
                readOnly={truncate || readOnly}
                value={location || ''}
                onBlur={() => onUpdateContentOption('location', location)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                  onUpdateContentOption('location', e.target.value)
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
